import dayjs from 'dayjs'

import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import type { TCommunityJoinParams } from '@/src/general/constants/routes'
import { useRoutes } from '@/src/general/utils/routes'

import type { TCommunityApiHasJoinedInfos, TMediumCommunityApi } from '../../api/communityApi.types'
import { COMMUNITY_ACTIVITY, COMMUNITY_LIFETIME } from '../../models/community.constants'
import type { TComputedSubscription } from '../../utils/getComputedSubscription'

export type TUseCommunitySubscriptionRouteProps = TCommunityJoinParams &
  TIdSlug & {
    activity?: TMediumCommunityApi['activity']
    lifetime?: TMediumCommunityApi['lifetime']
    hasJoined?: TCommunityApiHasJoinedInfos['hasJoined']
    subscription?: TComputedSubscription | TCommunityApiHasJoinedInfos['subscription']
  }

/**
 * Get community subscription route
 *
 * The function evaluates the type of community and its current activity to decide
 * whether the user should be redirected to select a date or to enable notifications.
 *
 * The logic is based on the following criteria:
 * - For communities with a `lifetime` of `novena` or `retreat`:
 *    - If the `activity` is `to_rediscover` or `ongoing`, the user is redirected to a screen
 *      to select a date for joining.
 *    - Otherwise (e.g., `activity` is `comingsoon`), the user is redirected to the community notifications screen.
 * - For communities with an `everlasting` `lifetime`, no date selection is necessary, and the
 *   user is redirected directly to the notifications screen.
 *
 * Based on the table:
 * - `lifetime: novena/retreat` + `activity: to_rediscover` -> Date selection screen (`scheduledDate = today`)
 * - `lifetime: novena/retreat` + `activity: ongoing` -> Date selection screen (`scheduledDate = null`)
 * - `lifetime: novena/retreat` + `activity: comingsoon` -> Notifications screen (`scheduledDate = null`)
 * - `lifetime: everlasting` -> Notifications screen (No date parameter)
 */
export const useCommunitySubscriptionRoute = ({
  activity,
  id,
  slug,
  hasJoined,
  lifetime,
  subscription,
  source,
  origin,
  course,
}: TUseCommunitySubscriptionRouteProps) => {
  const { routes } = useRoutes()

  const joinRouteParams = { id, slug, course, origin, source }

  if (!getIsLoggedIn()) return routes.communityLanding(joinRouteParams)

  if (!lifetime) return routes.communityJoin(joinRouteParams)

  const isRetreatOrNovena = lifetime === COMMUNITY_LIFETIME.NOVENA || lifetime === COMMUNITY_LIFETIME.RETREAT

  const isCompleted = subscription && 'nextPublication' in subscription && !subscription.nextPublication

  if (
    hasJoined &&
    (!isRetreatOrNovena ||
      (isRetreatOrNovena && subscription && dayjs().isBefore(subscription.toDate, 'day') && !isCompleted))
  )
    return routes.community({ id, slug, origin })

  const isActivityRequiringDateSelection =
    activity === COMMUNITY_ACTIVITY.TO_REDISCOVER || activity === COMMUNITY_ACTIVITY.ONGOING

  return isRetreatOrNovena && isActivityRequiringDateSelection
    ? routes.communityJoinDate(joinRouteParams)
    : routes.communityJoinNotifications(joinRouteParams)
}
